import React, { useState, useEffect } from "react";
import { Grid2 as Grid, Typography } from "@mui/material";
import { GetState, GetCity } from "react-country-state-city";
import { red } from "@mui/material/colors";
import { countries, timezoneMapping } from "../../../../../utils/helper";
import SelectInput from "../../../../../components/ui/SelectInput";
import InputField1 from "../../../../../components/ui/InputField1";
import { validateNumber } from "../../../../../utils/velidations";

function Address({ errors, persistedData, countryRef, stateRef, cityRef, timezone }) {
    const [selectedCountry, setSelectedCountry] = useState(null);
    const [selectedState, setSelectedState] = useState(null);
    const [selectedCity, setSelectedCity] = useState(null);
    const [selectedTimeZone, setSelectedTimeZone] = useState(null);
    const [stateList, setStateList] = useState([]);
    const [citiesList, setCitiesList] = useState([]);
    const [filteredTimeZones, setFilteredTimeZones] = useState([]);

    const stateHasCities = stateList.find(s =>
        String(s.id) === String(selectedState?.id) || s.name === selectedState?.name
    )?.hasCities ?? true;

    useEffect(() => {
        if (!persistedData) return;

        const countryVal = persistedData?.country;
        const stateVal = persistedData?.state;
        const cityVal = persistedData?.city;
        const tzValue = persistedData?.timeZone;

        if (countryVal) {
            const c = countries.find(item =>
                item.name === countryVal || String(item.id) === String(countryVal)
            );
            if (c) setSelectedCountry(c);
        }

        // We set these as objects with names so the SelectInput can display them 
        // while the useEffects below fetch the full lists and IDs
        if (stateVal) setSelectedState(typeof stateVal === 'string' ? { name: stateVal } : stateVal);
        if (cityVal) setSelectedCity(typeof cityVal === 'string' ? { name: cityVal } : cityVal);
        if (tzValue) setSelectedTimeZone({ name: tzValue, value: tzValue });

    }, [persistedData]);

    // 2. Handle Country Change: Fetch States & Filter Timezones
    useEffect(() => {
        if (selectedCountry?.id) {
            GetState(parseInt(selectedCountry.id)).then((result) => {
                setStateList(result);

                // If we have a state name from Edit Mode, find its ID in the fresh list
                if (selectedState?.name && !selectedState.id) {
                    const fullState = result.find(s => s.name === selectedState.name);
                    if (fullState) setSelectedState(fullState);
                }
            });

            // Filter Timezones by Country ISO Code
            const iso = selectedCountry.iso2;
            const rawZones = timezoneMapping[iso] || [];
            const formatted = rawZones.map(tz => ({ name: tz, value: tz }));
            setFilteredTimeZones(formatted);
        }
    }, [selectedCountry]);

    // 3. Handle State Change: Fetch Cities
    useEffect(() => {
        if (selectedCountry?.id && selectedState?.id) {
            GetCity(parseInt(selectedCountry.id), parseInt(selectedState.id)).then((result) => {
                setCitiesList(result);

                // If we have a city name from Edit Mode, find its ID in the fresh list
                if (selectedCity?.name && !selectedCity.id) {
                    const fullCity = result.find(c => c.name === selectedCity.name);
                    if (fullCity) setSelectedCity(fullCity);
                }
            });
        }
    }, [selectedCountry?.id, selectedState?.id, selectedCity?.name, selectedCity?.id]);

    const handleCountryChange = (_, value) => {
        const val = value?.props?.value || value;
        setSelectedCountry(val || null);
        setSelectedState(null);
        setSelectedCity(null);
        setSelectedTimeZone(null);
    };

    const handleStateChange = (_, value) => {
        const val = value?.props?.value || value;
        setSelectedState(val || null);
        setSelectedCity(null);
    };

    const handleCityChange = (_, value) => {
        const val = value?.props?.value || value;
        setSelectedCity(val || null);
    };

    const handleTimeZoneChange = (_, value) => {
        const val = value?.props?.value || value;
        setSelectedTimeZone(val || null);
    };

    return (
        <>
            <Grid size={12}>
                <Typography sx={{ fontSize: "20px", fontWeight: "600", color: "other.color1", mb: 2, mt: 2 }}>
                    Address & Timezone Information
                </Typography>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
                <SelectInput
                    ref={countryRef}
                    name="country"
                    label="Country *"
                    options={countries}
                    renderValueKey="name"
                    selectLabel="Select country"
                    varianttype="selectType1"
                    onChange={handleCountryChange}
                    value={selectedCountry || null}
                />
                {errors?.country && <Typography mt={1} color={red["500"]}>Please select a country</Typography>}
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
                <SelectInput
                    ref={stateRef}
                    name="state"
                    label="State *"
                    options={stateList}
                    renderValueKey="name"
                    selectLabel="Select state"
                    varianttype="selectType1"
                    disabled={!selectedCountry}
                    onChange={handleStateChange}
                    value={selectedState || null}
                />
                {errors?.state && <Typography mt={1} color={red["500"]}>Please select a state</Typography>}
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
                <SelectInput
                    ref={cityRef}
                    name="city"
                    label="City *"
                    options={citiesList}
                    renderValueKey="name"
                    selectLabel="Select city"
                    varianttype="selectType1"
                    disabled={!selectedState}
                    onChange={handleCityChange}
                    value={selectedCity || null}
                />
                {stateHasCities && errors?.city && <Typography mt={1} color={red["500"]}>Please select a city</Typography>}
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
                <SelectInput
                    ref={timezone}
                    name="timeZone"
                    label="Timezone *"
                    options={filteredTimeZones}
                    renderValueKey="name"
                    varianttype="selectType1"
                    selectLabel="Select a timezone"
                    onChange={handleTimeZoneChange}
                    value={selectedTimeZone || null}
                    // Key forces re-render when country changes to reset the selection
                    key={`tz-${selectedCountry?.id || 'none'}`}
                />
                {errors?.timeZone && (
                    <Typography mt={1} color={red["500"]}>
                        Please select time zone
                    </Typography>
                )}
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
                <InputField1
                    name="zip"
                    errors={errors}
                    label="Zip Code *"
                    errText={typeof errors?.zip === "string" ? errors.zip : "Please enter zip code"}
                    placeholder="Enter zip code"
                    defaultValue={persistedData?.zip || ""}
                    key={`zip-${persistedData?.zip || 'empty'}`}
                    onChangeFun={validateNumber}
                    slotProps={{ inputProps: { maxLength: 6 } }}
                />
            </Grid>
        </>
    );
}

export default Address;